<template>
  <v-container
    id="testing"
    fluid
    tag="section"
  >
    <v-row justify="center">
      <v-col
        cols="12"
        md="8"
      >
        <base-material-card
          v-if="$can('write', 'testing')"
          icon="mdi-test-tube-empty"
        >
          <template #after-heading>
            <div class="font-weight-light card-title mt-2">
              {{ $t('testing.selecttestuser') }}
            </div>
          </template>
          <v-combobox
            v-model="testers"
            :items="availableUsers"
            item-text="fullName"
            item-value="id"
            :label="$t('testing.list')"
            multiple
            hide-selected
            append-outer-icon="mdi-plus-circle"
            @click:append-outer="showSaveUserDialog = true"
          />
          <v-data-table
            :headers="tableHeader"
            :items="testers"
            :footer-props="{
              'items-per-page-options': [5, 10, 15, 20, 25, -1]
            }"
            :items-per-page="5"
            class="elevation-0"
          >
            <template #footer.page-text>
              {{ testers.length }}
              <span v-if="testers.length < 2">user selected</span>
              <span v-else>users selected</span>
              <v-btn
                color="green darken-1"
                class="ma-2"
                :disabled="testers.length < 1"
                text
                @click="clearTesters"
              >
                {{ $t('testing.clear') }}
              </v-btn>
            </template>
            <template #item.actions="{ item }">
              <span class="showOnHover mr-2">
                <v-icon
                  @click="removeUser(item)"
                >mdi-account-remove</v-icon>
              </span>
              <span class="showOnHover">
                <v-icon
                  @click="confirmDeleteUser(item)"
                >mdi-trash-can-outline</v-icon>
              </span>
            </template>
          </v-data-table>
        </base-material-card>
        <base-material-card
          v-if="$can('write', 'testing')"
          icon="mdi-test-tube-empty"
        >
          <template #after-heading>
            <div class="font-weight-light card-title mt-2">
              {{ $t('testing.createscenario') }}
            </div>
          </template>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="title"
                class="purple-input"
                :label="$t('testing.title')"
                :readonly="isPublished"
                @input="validateTestForm"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-menu
                ref="testDateMenu"
                v-model="showTestDatePicker"
                :close-on-content-click="false"
                :return-value.sync="testDate"
                transition="scale-transition"
                offset-y
                min-width="auto"
                :disabled="isPublished"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="testDate"
                    class="purple-input"
                    :label="$t('testing.testingdate')"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="testDate"
                  no-title
                  scrollable
                >
                  <v-spacer />
                  <v-btn
                    text
                    color="primary"
                    @click="showTestDatePicker = false"
                  >
                    {{ $t('testing.cancel') }}
                  </v-btn>
                  <v-btn
                    text
                    color="primary"
                    @click="$refs.testDateMenu.save(testDate)"
                  >
                    {{ $t('testing.save') }}
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              md="3"
              style="display:flex"
            >
              <v-text-field
                class="purple-input"
                :label="$t('testing.projectid')"
                readonly
                :value="id + (isPublished?' (published)':'') || '[id]'"
              />
              <v-icon
                @click="showLoadProjectDialog = true"
              >
                mdi-folder-download-outline
              </v-icon>
            </v-col>
            <v-col
              cols="12"
              md="12"
            >
              <v-text-field
                v-model="author"
                class="purple-input mr-4"
                :label="$t('testing.author')"
                :readonly="isPublished"
                @input="validateTestForm"
              />
            </v-col>
            <v-col
              cols="12"
              md="12"
            >
              <tiptap-vuetify
                v-model="notice"
                :extensions="tiptapExtensions"
                :card-props="{ flat: true }"
                :disabled="isPublished"
                @input="validateTestForm"
              >
                <template #toolbar-before>
                  <div class="tiptapHeader">
                    {{ $t('testing.desc') }}
                  </div>
                </template>
              </tiptap-vuetify>
            </v-col>
            <v-col cols="12">
              <v-data-table
                :headers="questionTableHeader"
                :items="questionSet"
                :footer-props="{
                  'items-per-page-options': [5, 10, 15, 20, 25, -1]
                }"
                :items-per-page="-1"
                class="elevation-0"
              >
                <template #no-data>
                  <v-btn
                    color="green darken-1"
                    class="ma-2"
                    text
                    :disabled="isPublished"
                    @click="addNewQuestion"
                  >
                    {{ $t('testing.newquestion') }}
                  </v-btn>
                </template>
                <template #footer.page-text>
                  {{ questionSet.length }}
                  <span v-if="questionSet.length < 2">question</span>
                  <span v-else>questions</span>
                  <v-btn
                    color="green darken-1"
                    class="ma-2"
                    text
                    :disabled="isPublished"
                    @click="addNewQuestion"
                  >
                    {{ $t('testing.newquestion') }}
                  </v-btn>
                  <v-btn
                    color="green darken-1"
                    class="ma-2"
                    :disabled="questionSet.length < 1 || isPublished"
                    text
                    @click="clearQuestions"
                  >
                    {{ $t('testing.clearquestion') }}
                  </v-btn>
                </template>
                <template #item.id="{ item }">
                  {{ item.id + 1 }})
                </template>
                <template
                  v-if="!isPublished"
                  #item.question="{ item }"
                >
                  <v-edit-dialog
                    :return-value.sync="item.question"
                    large
                  >
                    {{ item.question }}
                    <template #input>
                      <v-text-field
                        v-model="item.question"
                        :label=" $t('testing.edit')"
                        single-line
                        @input="validateTestForm"
                      />
                    </template>
                  </v-edit-dialog>
                </template>
                <template #item.isYesNo="{ item }">
                  <v-checkbox
                    v-model="item.isYesNo"
                    :readonly="isPublished"
                    @change="validateTestForm"
                  />
                </template>
              </v-data-table>
            </v-col>
            <v-col
              cols="12"
              class="text-right"
            >
              <v-btn
                color="green darken-1"
                :disabled="!id"
                @click="copyAsNew"
              >
                {{ $t('testing.copy') }}
              </v-btn>
              <v-btn
                color="green darken-1"
                :disabled="isSaveButtonDisabled || isPublished"
                @click="saveTest"
              >
                {{ $t('testing.save') }}
              </v-btn>
              <v-btn
                color="green darken-1"
                :disabled="isPublishButtonDisabled"
                @click="publishTest"
              >
                <span v-if="isPublished">
                  {{ $t('testing.resendemail') }}
                </span>
                <span v-else>
                  {{ $t('testing.publish') }}
                </span>
              </v-btn>
            </v-col>
          </v-row>
        </base-material-card>
      </v-col>
    </v-row>
    <template>
      <v-row justify="center">
        <v-dialog
          v-model="showSaveUserDialog"
          persistent
          max-width="500"
        >
          <v-card>
            <v-card-title class="text-h5">
              {{ $t('testing.savenewuser') }}
            </v-card-title>
            <v-card-text>
              <v-form>
                <v-row>
                  <v-col
                    cols="12"
                  >
                    <v-text-field
                      v-model="email"
                      class="purple-input"
                      :label=" $t('testing.email')"
                      @input="validateForm"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                  >
                    <v-text-field
                      v-model="lastName"
                      class="purple-input"
                      :label=" $t('testing.lastname')"
                      @input="validateForm"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                  >
                    <v-text-field
                      v-model="firstName"
                      class="purple-input"
                      :label=" $t('testing.firstname')"
                      @input="validateForm"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="green darken-1"
                text
                @click="closeSaveUserDialog"
              >
                {{ $t('testing.cancel') }}
              </v-btn>
              <v-btn
                color="green darken-1"
                text
                @click="saveUser"
              >
                {{ $t('testing.save') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </template>
    <template>
      <v-row justify="center">
        <v-dialog
          v-model="showDeleteDialog"
          persistent
          max-width="500"
        >
          <v-card>
            <v-card-title class="text-h5">
              {{ $t('testing.deletingtester') }}
            </v-card-title>
            <v-card-text>
              {{ $t('testing.confirmdeletingtester') }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="green darken-1"
                text
                @click="showDeleteDialog = false"
              >
                {{ $t('testing.cancel') }}
              </v-btn>
              <v-btn
                color="green darken-1"
                text
                @click="deleteUser"
              >
                {{ $t('testing.deleteok') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </template>
    <template>
      <v-row justify="center">
        <v-dialog
          v-model="showLoadProjectDialog"
          persistent
          max-width="500"
        >
          <v-card>
            <v-card-title class="text-h5">
              {{ $t('testing.loadscenario') }}
            </v-card-title>
            <v-card-text>
              <v-text-field
                v-model="loadId"
                class="purple-input"
                :label="$t('testing.projectid')"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="green darken-1"
                text
                @click="showLoadProjectDialog = false"
              >
                {{ $t('testing.cancel') }}
              </v-btn>
              <v-btn
                color="green darken-1"
                text
                @click="showLoadProjectDialog = false; loadTest()"
              >
                {{ $t('testing.save') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </template>
    <template>
      <v-row justify="center">
        <v-dialog
          v-model="showSaveProjectSuccess"
          persistent
          max-width="500"
        >
          <v-card>
            <v-card-title class="text-h5">
              {{ $t('testing.saved') }}
            </v-card-title>
            <v-card-text>
              {{ $t('testing.projecttitle') }}: {{ title }}<br>
              {{ $t('testing.projectid') }}: {{ id }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="green darken-1"
                text
                @click="showSaveProjectSuccess = false"
              >
                {{ $t('testing.close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </template>
  </v-container>
</template>

<script>
  import { createHash } from 'crypto'
  import { TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Code, Paragraph, BulletList, OrderedList, ListItem, Link, Blockquote, HardBreak, HorizontalRule, History, Image, CodeBlock } from 'tiptap-vuetify'
  import { getFirebaseToken, writeTestUser, fetchTestUser, deleteTestUser, writeTestScenario, fetchTestScenario, writeTest } from '@/firebase'
  import uploadImage from './modals/uploadImage'
  export default {
    components: { TiptapVuetify },
    data: () => ({

      testers: [],
      availableUsers: [],
      showSaveUserDialog: false,
      showDeleteDialog: false,
      lastName: '',
      firstName: '',
      email: '',
      deletionTargetUser: '',

      id: '',
      isPublished: false,
      title: '',
      testDate: new Date().toISOString().substr(0, 10),
      notice: '',
      author: '',
      tiptapExtensions: [
        History,
        Blockquote,
        Link,
        [Image, {
          options: {
            imageSourcesOverride: true,
            imageSources: [
              { component: uploadImage, name: 'Upload Image' },
            ],
          },
        }],
        Bold,
        Italic,
        Underline,
        Strike,
        ListItem,
        BulletList,
        OrderedList,
        [Heading, {
          options: {
            levels: [1, 2, 3],
          },
        }],
        Code,
        CodeBlock,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
      questionSet: [],
      isSaveButtonDisabled: true,
      isPublishButtonDisabled: true,
      showLoadProjectDialog: false,
      showSaveProjectSuccess: false,
      loadId: '',
      showTestDatePicker: false,
    }),
    computed: {
      questionTableHeader () {
        return [
          {
            text: '#',
            value: 'id',
            width: '50px',
          },
          {
            text: this.$t('testing.questionlist'),
            value: 'question',
            width: '70%',
          },
          {
            text: this.$t('testing.publish'),
            value: 'isYesNo',
          },
        ]
      },
      tableHeader () {
        return [
          {
            text: this.$t('testing.lastname'),
            value: 'lastName',
          },
          {
            text: this.$t('testing.firstname'),
            value: 'firstName',
          },
          {
            text: this.$t('testing.email'),
            value: 'email',
          },
          {
            text: this.$t('testing.projectid'),
            value: 'id',
          },
          {
            text: '',
            value: 'actions',
          },
        ]
      },
    },
    mounted () {
      this.loadTable()
      this.author = this.$store.state.user.user.firstName + ' ' + this.$store.state.user.user.lastName
    },
    methods: {
      saveUser () {
        if (this.$can('write', 'testing') && this.validateForm() === true) {
          const id = createHash('sha256').update(this.email).digest('hex').substr(0, 8)
          const payload = {
            id: id,
            lastName: this.lastName,
            firstName: this.firstName,
            email: this.email,
          }
          writeTestUser(payload).then(() => {
            this.loadTable()
          })
          this.closeSaveUserDialog()
        }
      },
      closeSaveUserDialog () {
        this.showSaveUserDialog = false
        this.lastName = ''
        this.firstName = ''
        this.email = ''
      },
      validateForm () {
        if (this.lastName && this.firstName && this.email) {
          return true
        }
        return false
      },
      clearTesters () {
        this.testers = []
      },
      removeUser (user) {
        const index = this.testers.indexOf(user)
        if (index > -1) {
          this.testers.splice(index, 1)
        }
        console.log(this.testers)
      },
      confirmDeleteUser (user) {
        this.showDeleteDialog = true
        this.deletionTargetUser = user
      },
      deleteUser () {
        if (this.$can('write', 'testing')) {
          this.removeUser(this.deletionTargetUser)
          deleteTestUser(this.deletionTargetUser.id).then(() => {
            this.loadTable()
          })
          this.deletionTargetUser = ''
          this.showDeleteDialog = false
        }
      },
      addNewQuestion () {
        this.questionSet.push({ id: this.questionSet.length, question: 'question #' + (this.questionSet.length + 1), isYesNo: false })
        this.validateTestForm()
      },
      clearQuestions () {
        this.questionSet = []
        this.validateTestForm()
      },
      clearTestForm () {
        this.id = ''
        this.title = ''
        this.testDate = new Date().toISOString().substr(0, 10)
        this.author = this.$store.state.user.user.firstName + ' ' + this.$store.state.user.user.lastName
        this.notice = ''
        this.questionSet = []
        this.validateTestForm()
      },
      validateTestForm () {
        this.isSaveButtonDisabled = false
        this.isPublishButtonDisabled = false
        if (!this.title || !this.author) {
          this.isSaveButtonDisabled = true
        }

        if (!this.id || this.isSaveButtonDisabled || !this.notice || !this.testDate || this.questionSet.length < 1 || this.testers.length < 1) {
          this.isPublishButtonDisabled = true
        }
      },
      copyAsNew () {
        this.id = ''
        this.isPublished = false
      },
      saveTest () {
        if (this.$can('write', 'testing')) {
          if (!this.id) {
            this.id = createHash('sha256').update('' + Date.now()).digest('hex').substr(0, 8)
          }

          const payload = {
            id: this.id,
            isPublished: this.isPublished,
            title: this.title,
            testDate: this.testDate,
            author: this.author,
            notice: this.notice,
            questionSet: this.questionSet,
            testers: this.testers,
          }
          writeTestScenario(payload)
            .then(() => {
              this.showSaveProjectSuccess = true
            })
            .catch(error => {
              console.log(error)
            })
        }
      },
      loadTest () {
        if (this.$can('read', 'testing')) {
          if (this.loadId) {
            fetchTestScenario(this.loadId.toLowerCase().trim()).then(results => {
              this.id = results.id
              this.isPublished = results.isPublished
              this.title = results.title
              this.testDate = results.testDate
              this.author = results.author
              this.notice = results.notice
              this.questionSet = results.questionSet
              this.testers = results.testers
              this.validateTestForm()
            })
              .catch(error => {
                console.log(error)
              })
          }
        }
      },
      publishTest () {
        if (this.$can('write', 'testing')) {
          const payload = {
            id: this.id,
            title: this.title,
            author: this.author,
            notice: this.notice,
            questionSet: this.questionSet,
            testers: {},
          }

          this.testers.forEach((tester) => {
            payload.testers[tester.id] = { tester: tester, answers: [] }
            const message = 'Hello ' + tester.firstName + ' ' + tester.lastName + ',<br /><br />' +
              this.notice + '<br /><br />' +
              `${process.env.VUE_APP_DOMAIN}/#/testing/` + this.id + '/' + tester.id
            this.sendMail(tester.email, this.title + ' at ' + this.testDate, message)
          })

          if (!this.isPublished) {
            this.isPublished = true
            this.saveTest()
            writeTest(payload)
              .catch(error => {
                console.log(error)
              })
          }
        }
      },
      async sendMail (email, subject, message) {
        // local: http://localhost:5001/voffice-dashboard/us-central1/sendEmail
        // cloud: https://us-central1-voffice-dashboard.cloudfunctions.net/sendEmail
        fetch(`${process.env.VUE_APP_FUNCTIONS}/sendEmail`, {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + (await getFirebaseToken()),
            'Content-Type': 'application/json;charset=UTF-8',
          },
          body: JSON.stringify({
            email: email,
            from: 'vOffice Test-Team <hello@voffice.pro>',
            replyTo: 'hello@voffice.pro',
            subject: subject,
            message: message,
          }),
        })
      },
      loadTable () {
        if (this.$can('read', 'testing')) {
          fetchTestUser('all').then(results => {
            this.availableUsers = results
            this.availableUsers.forEach((user, index) => {
              this.availableUsers[index].actions = ''
              this.availableUsers[index].fullName = user.firstName + ' ' + user.lastName
            })
          })
        }
      },
    },
  }
</script>

<style scoped lang="scss">
.showOnHover{
  .v-icon {
    //color: transparent;
    color: #D3D3D3;
      &:hover {
        color: red;
    }
  }
}
.tiptapHeader {
  font-size:11px;
  color:rgba(0,0,0,.40);
}
</style>
